import {
  MonitorSmartphone,
  Sparkles,
  Home,
  Database,
  FolderOpen,
  Table2,
  Link2,
  Workflow,
  Building2,
  Users,
  Activity,
  AlertTriangle,
  Map,
  Upload,
  Shield,
  ShieldAlert,
  Layers,
  BarChart3,
  Radio,
  Settings,
  Wand2,
  Inbox,
  FileText,
  RotateCcw,
  RefreshCw,
  GitMerge,
  Key,
  Lock,
  Rss,
  ClipboardCheck,
  HeartPulse,
  Bell,
  Cloud,
  GitBranch,
  Mail,
  MessageSquare,
  FileCode,
} from "lucide-react";
import type { NavItem, SearchableItem, SearchCategory } from "@nuance/ui";
import type { ComponentType } from "react";

// ── Product Config Type ──────────────────────────────────────────

export interface ProductConfig {
  id: "elements" | "forge" | "edge" | "vault";
  appName: string;
  appSubtitle: string;
  logoIcon: ComponentType<{ className?: string }>;
  logoGradient: string;
  navItems: NavItem[];
  pageRouteMap: Record<string, string>;
  searchCategories: SearchCategory[];
  searchItems: SearchableItem[];
  getBreadcrumbs: (page: string) => { label: string; page?: string }[];
  /**
   * If set, the ProductSwitcher opens this URL in a new tab instead of
   * navigating internally. Used for products that live in their own SWA
   * (Edge, Vault). Internal product routes still work for direct URL hits;
   * the SWA-level redirects on /edge/* funnel them to the new app.
   */
  externalUrl?: string;
}

// ── Elements Config ──────────────────────────────────────────────

export const elementsConfig: ProductConfig = {
  id: "elements",
  appName: "NUANCE ELEMENTS",
  appSubtitle: "Management Hub",
  logoIcon: Shield,
  logoGradient: "from-emerald-500 to-teal-600",
  navItems: [
    {
      id: "organization",
      label: "Organization",
      icon: Building2,
      type: "section",
      children: [
        { id: "access", label: "Access", icon: Shield },
        { id: "ai-governance", label: "AI Governance", icon: Sparkles },
        // stays until Omni shuts down (FORGE-411)
        { id: "users", label: "Users", icon: Users },
      ],
    },
    {
      id: "platform-features",
      label: "Platforms",
      icon: Layers,
      type: "single",
    },
    {
      id: "connections",
      label: "Connections",
      icon: Link2,
      type: "section",
      children: [
        { id: "database-connections", label: "Databases", icon: Database },
        { id: "api-connections", label: "APIs", icon: Link2 },
        { id: "feed-registry", label: "Feed Registry", icon: Rss },
        { id: "key-management", label: "Key Management", icon: Key },
        { id: "sms-routing", label: "SMS Routing", icon: MessageSquare },
      ],
    },
    {
      id: "communications",
      label: "Communications",
      icon: Mail,
      type: "section",
      children: [
        { id: "comms-campaigns", label: "Campaigns", icon: Mail },
        { id: "comms-saved-segments", label: "Saved Segments", icon: Database },
        { id: "comms-contacts", label: "Contacts", icon: Users },
        { id: "comms-templates", label: "Templates", icon: FileText },
        { id: "comms-suppressions", label: "Suppressions", icon: Shield },
      ],
    },
    {
      id: "operations",
      label: "Operations",
      icon: Activity,
      type: "section",
      children: [
        { id: "activity", label: "Activity Log", icon: Activity },
        { id: "notifications", label: "Notifications", icon: Bell },
        { id: "health", label: "Database Health", icon: HeartPulse },
        { id: "platform-status", label: "Platform Status", icon: Radio },
        { id: "failover", label: "Failover", icon: GitBranch },
      ],
    },
    {
      id: "security",
      label: "Security",
      icon: ShieldAlert,
      type: "section",
      children: [
        { id: "security-findings", label: "Findings", icon: AlertTriangle },
      ],
    },
    {
      id: "operations",
      label: "Operations",
      icon: Activity,
      type: "section",
      children: [
        { id: "service-health", label: "Service Health", icon: Activity },
      ],
    },
    { id: "admin-access", label: "Admin Access", icon: Lock, type: "single" },
  ],
  pageRouteMap: {
    projects: "/elements/projects",
    "project-detail": "/elements/projects/:id",
    clients: "/elements/clients",
    "ai-governance": "/elements/ai-governance",
    "platform-features": "/elements/platform-features",
    "access-review": "/elements/access-review",
    platforms: "/elements/platforms",
    access: "/elements/access",
    "sms-routing": "/elements/sms-routing",
    "client-groups": "/elements/client-groups",
    users: "/elements/users",
    activity: "/elements/activity",
    "database-connections": "/elements/database-connections",
    "api-connections": "/elements/api-connections",
    "feed-registry": "/elements/feed-registry",
    "key-management": "/elements/key-management",
    "security-findings": "/elements/security-findings",
    "service-health": "/elements/service-health",
    "admin-access": "/elements/admin-access",
    "platform-status": "/elements/platform-status",
    health: "/elements/health",
    notifications: "/elements/notifications",
    failover: "/elements/failover",
    "comms-templates": "/elements/comms/templates",
    "comms-template-detail": "/elements/comms/templates/:id",
    "comms-campaigns": "/elements/comms/campaigns",
    "comms-campaign-new": "/elements/comms/campaigns/new",
    "comms-campaign-detail": "/elements/comms/campaigns/:id",
    "comms-contacts": "/elements/comms/contacts",
    "comms-contact-detail": "/elements/comms/contacts/:id",
    "comms-suppressions": "/elements/comms/suppressions",
    "comms-saved-segments": "/elements/comms/saved-segments",
  },
  searchCategories: [
    {
      id: "page",
      label: "Pages",
      icon: Home,
      color:
        "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400",
    },
    {
      id: "action",
      label: "Actions",
      icon: Workflow,
      color:
        "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400",
    },
  ],
  searchItems: [
    {
      id: "p-projects",
      type: "page",
      name: "Projects",
      description: "In Access: projects and their dimensions",
      page: "access",
      icon: Layers,
    },
    {
      id: "p-clients",
      type: "page",
      name: "Clients",
      description: "Add or change a client (in Access)",
      page: "access",
      icon: Building2,
    },
    {
      id: "p-access",
      type: "page",
      name: "Access",
      description:
        "Client groups, clients, modules and people, reviewed before saving",
      page: "access",
      icon: Key,
    },
    {
      id: "p-setup",
      type: "page",
      name: "Clients & Access (now Access)",
      description: "Retired: its tools are in Access",
      page: "access",
      icon: Wand2,
    },
    {
      id: "p-platforms",
      type: "page",
      name: "Platforms",
      description:
        "In Access: Factor, Edge, Trulero, Omni, their modules, the projects that feed them, the client groups that use them",
      page: "access",
      icon: MonitorSmartphone,
    },
    {
      id: "p-access-review",
      type: "page",
      name: "Access checks",
      description:
        "Users > Checks: Postgres vs Snowflake differences and duplicate accounts",
      page: "users",
      icon: ClipboardCheck,
    },
    {
      id: "p-ai-governance",
      type: "page",
      name: "AI Governance",
      description:
        "AI assistant policies, plans, credits, prices and usage (Factor + Edge)",
      page: "ai-governance",
      icon: Sparkles,
    },
    {
      id: "p-sms-routing",
      type: "page",
      name: "SMS Routing",
      description: "Twilio number -> client Teams channel + forward email",
      page: "sms-routing",
      icon: MessageSquare,
    },
    {
      id: "p-users",
      type: "page",
      name: "Users",
      description: "Portal user management, roles, and Omni embed settings",
      page: "users",
      icon: Users,
    },
    {
      id: "p-activity",
      type: "page",
      name: "Activity Log",
      description: "System events and audit trail",
      page: "activity",
      icon: Activity,
    },
    {
      id: "p-db-conn",
      type: "page",
      name: "Database Connections",
      description: "Manage ETL and Edge database connections",
      page: "database-connections",
      icon: Database,
    },
    {
      id: "p-api-conn",
      type: "page",
      name: "API Connections",
      description: "Manage API, storage, and webhook connections",
      page: "api-connections",
      icon: Link2,
    },
    {
      id: "p-feeds",
      type: "page",
      name: "Feed Registry",
      description: "Manage data feed schedules, ownership, and automation",
      page: "feed-registry",
      icon: Rss,
    },
    {
      id: "p-key-mgmt",
      type: "page",
      name: "Key Management",
      description:
        "Monitor and rotate secrets stored in Azure Key Vault and Entra ID",
      page: "key-management",
      icon: Key,
    },
    {
      id: "p-security-findings",
      type: "page",
      name: "Security Findings",
      description:
        "Open security findings from the secret-audit + GitHub alert scanners",
      page: "security-findings",
      icon: ShieldAlert,
    },
    {
      id: "p-admin-access",
      type: "page",
      name: "Admin Access",
      description: "Nuance admin access control — project and client grants",
      page: "admin-access",
      icon: Lock,
    },
    {
      id: "p-platform-status",
      type: "page",
      name: "Platform Status",
      description: "Status banner, failover connections, and health monitoring",
      page: "platform-status",
      icon: Radio,
    },
    {
      id: "p-health",
      type: "page",
      name: "Database Health",
      description:
        "Credit usage, query patterns, warehouse monitoring, and alerts",
      page: "health",
      icon: HeartPulse,
    },
    {
      id: "p-notifications",
      type: "page",
      name: "Notifications",
      description:
        "System events: pipeline failures, schema changes, key alerts",
      page: "notifications",
      icon: Bell,
    },
    {
      id: "p-failover",
      type: "page",
      name: "Failover",
      description:
        "View and trigger Azure Front Door region failover (Central US ↔ East US 2)",
      page: "failover",
      icon: GitBranch,
    },
    {
      id: "p-comms-campaigns",
      type: "page",
      name: "Campaigns",
      description:
        "Send and schedule communications — pick a template, segment, preview, send.",
      page: "comms-campaigns",
      icon: Mail,
    },
    {
      id: "p-comms-contacts",
      type: "page",
      name: "Contacts",
      description: "Recipient roster — search, filter by status, add/edit",
      page: "comms-contacts",
      icon: Users,
    },
    {
      id: "p-comms-templates",
      type: "page",
      name: "Templates",
      description: "Registered email templates for the Nuance Comms Platform",
      page: "comms-templates",
      icon: FileText,
    },
    {
      id: "p-comms-saved-segments",
      type: "page",
      name: "Saved Segments",
      description: "Named, reusable audience queries for the campaign wizard",
      page: "comms-saved-segments",
      icon: Database,
    },
    {
      id: "p-comms-suppressions",
      type: "page",
      name: "Suppressions",
      description:
        "Do-not-send list — bounces, complaints, unsubscribes, manual adds",
      page: "comms-suppressions",
      icon: Shield,
    },
  ],
  getBreadcrumbs: (page: string) => {
    const map: Record<string, { label: string; page?: string }[]> = {
      projects: [{ label: "Projects" }],
      "project-detail": [
        { label: "Access", page: "access" },
        { label: "Project" },
      ],
      clients: [{ label: "Clients" }],
      "ai-governance": [{ label: "AI Governance" }],
      "platform-features": [{ label: "Platforms" }],
      "access-review": [{ label: "Access review" }],
      platforms: [{ label: "Platforms" }],
      access: [{ label: "Access" }],
      "sms-routing": [{ label: "SMS Routing" }],
      "client-groups": [{ label: "Client Groups" }],
      users: [{ label: "Users" }],
      activity: [{ label: "Activity Log" }],
      "database-connections": [{ label: "Database Connections" }],
      "api-connections": [{ label: "API Connections" }],
      "feed-registry": [{ label: "Feed Registry" }],
      "key-management": [{ label: "Key Management" }],
      "security-findings": [{ label: "Security" }, { label: "Findings" }],
      "service-health": [{ label: "Operations" }, { label: "Service Health" }],
      "admin-access": [{ label: "Admin Access" }],
      "platform-status": [{ label: "Platform Status" }],
      health: [{ label: "Database Health" }],
      notifications: [{ label: "Notifications" }],
      failover: [{ label: "Failover" }],
      "comms-templates": [{ label: "Communications" }, { label: "Templates" }],
      "comms-template-detail": [
        { label: "Communications" },
        { label: "Templates", page: "comms-templates" },
        { label: "Template Detail" },
      ],
      "comms-campaigns": [{ label: "Communications" }, { label: "Campaigns" }],
      "comms-campaign-new": [
        { label: "Communications" },
        { label: "Campaigns", page: "comms-campaigns" },
        { label: "New Campaign" },
      ],
      "comms-campaign-detail": [
        { label: "Communications" },
        { label: "Campaigns", page: "comms-campaigns" },
        { label: "Campaign Detail" },
      ],
      "comms-contacts": [{ label: "Communications" }, { label: "Contacts" }],
      "comms-contact-detail": [
        { label: "Communications" },
        { label: "Contacts", page: "comms-contacts" },
        { label: "Contact Detail" },
      ],
      "comms-suppressions": [
        { label: "Communications" },
        { label: "Suppressions" },
      ],
      "comms-saved-segments": [
        { label: "Communications" },
        { label: "Saved Segments" },
      ],
    };
    return map[page] ?? [{ label: page }];
  },
};

// ── Forge Config ─────────────────────────────────────────────────

export const forgeConfig: ProductConfig = {
  id: "forge",
  appName: "NUANCE FORGE",
  appSubtitle: "Data Pipeline",
  logoIcon: Layers,
  logoGradient: "from-blue-500 to-blue-600",
  navItems: [
    { id: "dashboard", label: "Dashboard", icon: Home, type: "single" },
    {
      id: "configure",
      label: "Configure",
      icon: Settings,
      type: "section",
      children: [
        { id: "sources", label: "Data Sources", icon: FolderOpen },
        {
          id: "cloud-feeds-wizard",
          label: "Wire Cloud Feeds Table",
          icon: Cloud,
        },
        { id: "source-templates", label: "Source Templates", icon: Layers },
        { id: "mapping", label: "Column Mapping", icon: Map },
        { id: "transforms", label: "Column Transforms", icon: Wand2 },
        { id: "report-types", label: "Report Types", icon: FileText },
        { id: "column-management", label: "Column Management", icon: Layers },
        { id: "pending-review", label: "Pending Review", icon: Inbox },
        { id: "models", label: "Models", icon: GitBranch },
      ],
    },
    {
      id: "data",
      label: "Data",
      icon: Database,
      type: "section",
      children: [
        { id: "upload", label: "Upload Files", icon: Upload },
        { id: "data-status", label: "Data Status", icon: Activity },
        { id: "master-tables", label: "Tables", icon: Table2 },
        { id: "table-joins", label: "Table Joins", icon: GitMerge },
        { id: "vault-sync", label: "Vault Sync", icon: Database },
        { id: "projection-recipes", label: "Recipes", icon: FileCode },
        { id: "ingestion-history", label: "Ingestion History", icon: Activity },
        { id: "history", label: "Upload History", icon: Activity },
        { id: "quarantine", label: "Error Quarantine", icon: AlertTriangle },
      ],
    },
    {
      id: "qa",
      label: "QA",
      icon: ClipboardCheck,
      type: "section",
      children: [
        {
          id: "qa-reconcile",
          label: "Data Reconciliation",
          icon: ClipboardCheck,
        },
      ],
    },
    {
      id: "recovery",
      label: "Recovery",
      icon: RotateCcw,
      type: "section",
      children: [
        { id: "rebuild", label: "Rebuild Data", icon: RefreshCw },
        { id: "rebuild-history", label: "Rebuild History", icon: Activity },
      ],
    },
  ],
  pageRouteMap: {
    dashboard: "/forge",
    sources: "/forge/sources",
    "source-wizard": "/forge/sources/new",
    "source-templates": "/forge/source-templates",
    mapping: "/forge/mapping",
    transforms: "/forge/transforms",
    "report-types": "/forge/report-types",
    "column-management": "/forge/column-management",
    "pending-review": "/forge/pending-review",
    models: "/forge/models",
    "master-tables": "/forge/master-tables",
    "reference-tables": "/forge/reference-tables",
    "table-joins": "/forge/table-joins",
    "vault-sync": "/forge/vault-sync",
    "projection-recipes": "/forge/projection-recipes",
    upload: "/forge/upload",
    history: "/forge/history",
    "data-status": "/forge/data-status",
    "ingestion-history": "/forge/ingestion-history",
    "cloud-feeds-wizard": "/forge/cloud-feeds/new",
    quarantine: "/forge/quarantine",
    "qa-reconcile": "/forge/qa",
    rebuild: "/forge/rebuild",
    "rebuild-history": "/forge/rebuild-history",
  },
  searchCategories: [
    {
      id: "page",
      label: "Pages",
      icon: Home,
      color: "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400",
    },
    {
      id: "data",
      label: "Data",
      icon: Database,
      color:
        "bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400",
    },
    {
      id: "action",
      label: "Actions",
      icon: Workflow,
      color:
        "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400",
    },
  ],
  searchItems: [
    {
      id: "p-dashboard",
      type: "page",
      name: "Dashboard",
      description: "Pipeline health and recent activity",
      page: "dashboard",
      icon: Home,
    },
    {
      id: "p-sources",
      type: "page",
      name: "Data Sources",
      description: "Configure data source connections and file patterns",
      page: "sources",
      icon: FolderOpen,
    },
    {
      id: "p-cf-wizard",
      type: "page",
      name: "Wire Cloud Feeds Table",
      description:
        "Wire a CF table into Forge — equivalent to committing a cf_specs file but via UI",
      page: "cloud-feeds-wizard",
      icon: Cloud,
    },
    {
      id: "p-source-templates",
      type: "page",
      name: "Source Templates",
      description: "Reusable source configurations for quick client onboarding",
      page: "source-templates",
      icon: Layers,
    },
    {
      id: "p-mapping",
      type: "page",
      name: "Column Mapping",
      description: "Configure column mappings between source and target",
      page: "mapping",
      icon: Map,
    },
    {
      id: "p-transforms",
      type: "page",
      name: "Column Transforms",
      description:
        "Configure cleansing and transform rules applied during merge",
      page: "transforms",
      icon: Wand2,
    },
    {
      id: "p-master",
      type: "page",
      name: "Master Tables",
      description: "View and manage master data tables",
      page: "master-tables",
      icon: Table2,
    },
    {
      id: "p-ref",
      type: "page",
      name: "Reference Tables",
      description: "DIM_ITEM, DIM_STORE, calendars",
      page: "reference-tables",
      icon: Link2,
    },
    {
      id: "p-joins",
      type: "page",
      name: "Table Joins",
      description:
        "Configure source and master-level joins with reference tables",
      page: "table-joins",
      icon: GitMerge,
    },
    {
      id: "p-recipes",
      type: "page",
      name: "Projection Recipes",
      description:
        "Author compute-in-Vault projection recipes (recipes as data)",
      page: "projection-recipes",
      icon: FileCode,
    },
    {
      id: "p-quarantine",
      type: "page",
      name: "Error Quarantine",
      description: "Review and resolve failed records",
      page: "quarantine",
      icon: AlertTriangle,
    },
    {
      id: "p-report-types",
      type: "page",
      name: "Report Types",
      description: "Configure report type definitions and natural keys",
      page: "report-types",
      icon: FileText,
    },
    {
      id: "p-col-mgmt",
      type: "page",
      name: "Column Management",
      description: "Add reference columns and propagate across sources",
      page: "column-management",
      icon: Layers,
    },
    {
      id: "p-pending-review",
      type: "page",
      name: "Pending Review",
      description: "Review and approve system suggestions",
      page: "pending-review",
      icon: Inbox,
    },
    {
      id: "p-models",
      type: "page",
      name: "Models",
      description: "Model outputs, published versions and value maps",
      page: "models",
      icon: GitBranch,
    },
    {
      id: "p-history",
      type: "page",
      name: "Upload History",
      description: "Full history of all data loads with filters and retry",
      page: "history",
      icon: Activity,
    },
    {
      id: "p-data-status",
      type: "page",
      name: "Data Status",
      description:
        "Is each client's data current, open problems, and every upload followed to reporting",
      page: "data-status",
      icon: Activity,
    },
    {
      id: "p-ingestion",
      type: "page",
      name: "Ingestion History",
      description:
        "Unified timeline of file uploads + Cloud Feeds runs across all sources",
      page: "ingestion-history",
      icon: Activity,
    },
    {
      id: "d-dc-metrics",
      type: "data",
      name: "DC Metrics",
      code: "dc_metrics",
      description: "Distribution Center Metrics report type",
      page: "sources",
      meta: "Report Type",
    },
    {
      id: "d-store-sales",
      type: "data",
      name: "Store Sales Inventory",
      code: "store_sales_inventory",
      description: "Store-level sales and inventory",
      page: "sources",
      meta: "Report Type",
    },
    {
      id: "d-ecomm-sales",
      type: "data",
      name: "eComm Sales",
      code: "ecomm_sales",
      description: "E-commerce sales data",
      page: "sources",
      meta: "Report Type",
    },
    {
      id: "d-ecomm-inv",
      type: "data",
      name: "eComm Inventory",
      code: "ecomm_inventory",
      description: "E-commerce inventory data",
      page: "sources",
      meta: "Report Type",
    },
    {
      id: "d-mumd",
      type: "data",
      name: "MUMD",
      code: "mumd",
      description: "Multi-UPC Multi-Distribution",
      page: "sources",
      meta: "Report Type",
    },
    {
      id: "d-master",
      type: "data",
      name: "MASTER_RETAIL",
      code: "RETAIL.MASTER_DATA.MASTER_RETAIL",
      description: "Single wide master table for all report types",
      page: "master-tables",
      meta: "Master Table",
    },
    {
      id: "d-dim-item",
      type: "data",
      name: "DIM_ITEM",
      code: "RETAIL.REFERENCE.DIM_ITEM",
      description: "Item Information reference table",
      page: "reference-tables",
      meta: "Reference Table",
    },
    {
      id: "d-dim-store",
      type: "data",
      name: "DIM_STORE",
      code: "RETAIL.REFERENCE.DIM_STORE",
      description: "Store Information reference table",
      page: "reference-tables",
      meta: "Reference Table",
    },
    {
      id: "a-upload",
      type: "action",
      name: "Upload Files",
      description: "Upload CSV, Excel, or ZIP files",
      page: "upload",
      icon: Upload,
    },
    {
      id: "a-new-source-pipeline",
      type: "action",
      name: "Create Pipeline",
      description: "Step-by-step source + pipeline creation wizard",
      page: "source-wizard",
      icon: Workflow,
    },
    {
      id: "a-new-source",
      type: "action",
      name: "Add Data Source",
      description: "Configure a new data source",
      page: "source-wizard",
      icon: FolderOpen,
    },
    {
      id: "p-qa",
      type: "page",
      name: "Data Reconciliation",
      description: "Verify raw table row counts match the master table",
      page: "qa-reconcile",
      icon: ClipboardCheck,
    },
    {
      id: "p-rebuild",
      type: "page",
      name: "Rebuild Data",
      description: "Recovery and data rebuild wizard",
      page: "rebuild",
      icon: RefreshCw,
    },
    {
      id: "p-rebuild-history",
      type: "page",
      name: "Rebuild History",
      description: "Past rebuild and recovery operations",
      page: "rebuild-history",
      icon: Activity,
    },
  ],
  getBreadcrumbs: (page: string) => {
    const map: Record<string, { label: string; page?: string }[]> = {
      dashboard: [{ label: "Dashboard" }],
      sources: [{ label: "Configure" }, { label: "Data Sources" }],
      "source-wizard": [
        { label: "Configure" },
        { label: "Data Sources", page: "sources" },
        { label: "New Source" },
      ],
      "source-templates": [
        { label: "Configure" },
        { label: "Source Templates" },
      ],
      mapping: [{ label: "Configure" }, { label: "Mapping" }],
      transforms: [{ label: "Configure" }, { label: "Column Transforms" }],
      upload: [{ label: "Data" }, { label: "Upload Files" }],
      "master-tables": [{ label: "Data" }, { label: "Master Tables" }],
      "reference-tables": [{ label: "Data" }, { label: "Reference Tables" }],
      "table-joins": [{ label: "Data" }, { label: "Table Joins" }],
      "vault-sync": [{ label: "Data" }, { label: "Vault Sync" }],
      "projection-recipes": [{ label: "Data" }, { label: "Recipes" }],
      history: [{ label: "Data" }, { label: "Upload History" }],
      quarantine: [{ label: "Data" }, { label: "Error Quarantine" }],
      "report-types": [{ label: "Configure" }, { label: "Report Types" }],
      "column-management": [
        { label: "Configure" },
        { label: "Column Management" },
      ],
      "pending-review": [{ label: "Configure" }, { label: "Pending Review" }],
      models: [{ label: "Configure" }, { label: "Models" }],
      "qa-reconcile": [{ label: "QA" }, { label: "Data Reconciliation" }],
      rebuild: [{ label: "Recovery" }, { label: "Rebuild Data" }],
      "rebuild-history": [{ label: "Recovery" }, { label: "Rebuild History" }],
    };
    return map[page] ?? [{ label: page }];
  },
};

// ── Edge Config (external) ───────────────────────────────────────
// The in-app Edge view builder was retired; Edge lives at its own app.
// This entry keeps the product switcher opening the external URL.

export const edgeConfig: ProductConfig = {
  id: "edge",
  appName: "NUANCE EDGE",
  appSubtitle: "BI Platform",
  logoIcon: BarChart3,
  logoGradient: "from-violet-500 to-purple-600",
  externalUrl: "https://edge.nuance.team",
  navItems: [],
  pageRouteMap: {},
  searchCategories: [],
  searchItems: [],
  getBreadcrumbs: () => [{ label: "Edge" }],
};

// ── Vault Config (external) ──────────────────────────────────────

export const vaultConfig: ProductConfig = {
  id: "vault",
  appName: "NUANCE VAULT",
  appSubtitle: "Data Storage & Query",
  logoIcon: Lock,
  logoGradient: "from-amber-500 to-orange-600",
  externalUrl: "https://vault.nuance.team",
  navItems: [],
  pageRouteMap: {},
  searchCategories: [],
  searchItems: [],
  getBreadcrumbs: () => [{ label: "Vault" }],
};

// ── All Products ─────────────────────────────────────────────────

export const allProducts = [
  elementsConfig,
  forgeConfig,
  edgeConfig,
  vaultConfig,
] as const;
